import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { formattedRoleAddress } from "../../components/formatted/FormattedText";
import DeleteModal from "../../components/modals/DeleteModal";
import { handleOpenModalClick } from "../../components/modals/EditableTable";
import { useTranslation } from "react-i18next";
const AddressRow = ({
  data,
  tableData,
  setTableData,
  setIsDeleted,
  formData,
  setFormData,
  setModalEdit,
  onOpenSite,
  variant = "default",
}) => {
  const { t } = useTranslation();
  const isSettingsList = variant === "settings";
  const [modalDelete, setModalDelete] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const actionsButtonRef = useRef(null);
  const nameRef = useRef(null);
  const roleRef = useRef(null);
  const [isNameTruncated, setIsNameTruncated] = useState(false);
  const [isRoleTruncated, setIsRoleTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = (ref, setter) => {
      if (ref.current) {
        setter(ref.current.scrollWidth > ref.current.clientWidth);
      }
    };

    const handleCheck = () => {
      checkTruncation(nameRef, setIsNameTruncated);
      checkTruncation(roleRef, setIsRoleTruncated);
    };

    handleCheck();
    window.addEventListener("resize", handleCheck);

    return () => {
      window.removeEventListener("resize", handleCheck);
    };
  }, [data]);

  const updateMenuPosition = () => {
    if (actionsButtonRef.current) {
      const rect = actionsButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.right - 120,
      });
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        actionsButtonRef.current &&
        !actionsButtonRef.current.contains(e.target) &&
        !e.target.closest(".table-standard-row-actions-menu-portal")
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleRowClick = (e) => {
    if (
      e.target.closest(".table-standard-row-actions") ||
      e.target.closest(".action-choice")
    ) {
      return;
    }
    if (typeof onOpenSite === "function") {
      onOpenSite(data);
      return;
    }
    handleOpenModalClick(data, formData, setFormData, setModalEdit);
  };

  return (
    <div
      className={`table-standard-row${isSettingsList ? " cursor-pointer" : ""}`}
      onClick={handleRowClick}
    >
      <div className="table-standard-row-cell" data-label={data.name}>
        <span
          ref={nameRef}
          className={
            isSettingsList
              ? "table-standard-row-cell-truncate text-sm font-semibold text-[#222222]"
              : "table-standard-row-cell-truncate"
          }
          title={isNameTruncated ? data.name : ""}
        >
          {data.name}
        </span>
      </div>

      <div className="table-standard-row-cell">
        <span
          ref={roleRef}
          className={
            isSettingsList
              ? "table-standard-row-cell-truncate text-sm text-[#5e5e5e]"
              : "table-standard-row-cell-truncate"
          }
          title={isRoleTruncated ? formattedRoleAddress(data.role) : ""}
        >
          {formattedRoleAddress(data.role)}
        </span>
      </div>

      {!isSettingsList && (
        <div className="table-standard-row-cell">
          <span
            className="table-standard-row-cell-truncate"
            title={`${data.number} ${data.street} ${data.postalCode} ${data.city}`}
          >
            {data.number +
              " " +
              data.street +
              " " +
              data.postalCode +
              " " +
              data.city}
          </span>
        </div>
      )}

      {!isSettingsList && (
        <div className="table-standard-row-cell table-standard-row-actions">
          <button
            ref={actionsButtonRef}
            type="button"
            className="table-standard-row-actions-dots"
            onClick={(e) => {
              e.stopPropagation();
              if (!isOpen && actionsButtonRef.current) {
                const rect = actionsButtonRef.current.getBoundingClientRect();
                setMenuPosition({
                  top: rect.bottom + 4,
                  left: rect.right - 120,
                });
              }
              setIsOpen(!isOpen);
            }}
            aria-label={t("Actions")}
            aria-expanded={isOpen}
          >
            <i
              className={isOpen ? "fas fa-times" : "fas fa-ellipsis-v"}
              aria-hidden
            />
          </button>
          {isOpen &&
            createPortal(
              <div
                className="table-standard-row-actions-menu-portal action-choice display-action"
                style={{
                  position: "fixed",
                  top: menuPosition.top,
                  left: menuPosition.left,
                  zIndex: 9999,
                  width: "auto",
                  minWidth: 120,
                  maxWidth: 200,
                  background: "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: 12,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  overflow: "hidden",
                }}
              >
                <button
                  type="button"
                  className="action"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (typeof onOpenSite === "function") {
                      onOpenSite(data);
                    } else {
                      handleOpenModalClick(
                        data,
                        formData,
                        setFormData,
                        setModalEdit,
                      );
                    }
                    setIsOpen(false);
                  }}
                >
                  {t("Edit")}
                </button>
                <button
                  type="button"
                  className="action"
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalDelete(true);
                    setIsOpen(false);
                  }}
                >
                  {t("Delete")}
                </button>
              </div>,
              document.body,
            )}
        </div>
      )}

      {!isSettingsList && modalDelete && (
        <DeleteModal
          tableData={tableData}
          setTableData={setTableData}
          modal={modalDelete}
          setModal={setModalDelete}
          id={data._id}
          message1={
            isSettingsList
              ? t("settingsSites.deleteConfirm", { name: data.name })
              : t("Do you really want to delete the address") +
                " : \n" +
                data.name +
                " ?"
          }
          message2={data.name}
          messageValidation={
            isSettingsList
              ? t("settingsSites.siteDeletedSuccess")
              : t("The address has been successfully deleted")
          }
          setIsDeleted={setIsDeleted}
          path={"address/delete/"}
        />
      )}
    </div>
  );
};

export default AddressRow;
